// Phase 5.4 · 간편 고객 뷰
// - 최근 CS 문의 5건 · 고객명 / 주문번호 / 문의유형 / 상태
// - [Customer 360] 버튼 → 기존 CustomersSection 렌더링 (상세 관리)
// - 요약만 표시 · 상세 pane 은 상세 관리에서
import React, { useEffect, useState } from 'react';
import { Loader2, User, MessageSquare, ChevronRight, Users } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '@/lib/api';

const CATEGORY_LABEL = {
  product: '상품', order: '주문', payment: '결제', delivery: '배송',
  cancel: '취소', exchange: '교환', return: '반품', other: '기타',
};
const STATUS_LABEL = {
  new: '신규', in_progress: '처리중', answered: '답변완료', closed: '종결',
};
const STATUS_CLS = {
  new: 'bg-[#FF3B30]/10 text-[#FF3B30]',
  in_progress: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  answered: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  closed: 'bg-gray-500/10 text-gray-500',
};

export default function SimpleCustomers({ onOpenDetailedSection, onOpenUnifiedDetail }) {
  const [inquiries, setInquiries] = useState([]);
  const [customerCount, setCustomerCount] = useState(null);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [rInq, rSum, rCust] = await Promise.all([
          api.get('/seller/inquiries', { params: { limit: 5, sort: 'created_desc' } }).catch(() => ({ data: [] })),
          api.get('/seller/inquiries/summary').catch(() => ({ data: {} })),
          api.get('/seller/customers', { params: { limit: 1 } }).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        // /seller/inquiries returns an array; envelopes tolerated defensively.
        const raw = rInq.data;
        const items = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
        setInquiries(items.slice(0, 5));
        setSummary(rSum.data || {});
        // /seller/customers returns an array — use length for total when unavailable.
        const cust = rCust.data;
        if (Array.isArray(cust))            setCustomerCount(cust.length);
        else if (typeof cust?.total === 'number') setCustomerCount(cust.total);
        else if (Array.isArray(cust?.items)) setCustomerCount(cust.items.length);
        else setCustomerCount(null);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-gray-500 py-16 justify-center" data-testid="simple-customers-loading"><Loader2 className="animate-spin" size={16} /> 불러오는 중…</div>;
  }

  return (
    <div className="space-y-5" data-testid="simple-customers-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400">고객</p>
          <h2 className="mt-1 text-2xl md:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            고객 & CS
          </h2>
          <p className="mt-1 text-xs text-gray-500">최근 CS 문의 요약과 Customer 360으로 바로 이동.</p>
        </div>
        <button onClick={onOpenDetailedSection} className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF3B30] text-white text-sm font-bold rounded-full" data-testid="simple-customers-360-btn">
          <User size={14} /> Customer 360 (상세 관리)
        </button>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="simple-customers-summary">
        <SumBox label="전체 고객" value={customerCount ?? '-'} testId="cust-sum-total" />
        <SumBox label="신규 문의" value={summary.new ?? summary.counts?.new ?? summary.open ?? 0} highlight testId="cust-sum-new" />
        <SumBox label="처리중" value={summary.in_progress ?? summary.counts?.in_progress ?? 0} testId="cust-sum-progress" />
        <SumBox label="답변완료" value={summary.answered ?? summary.counts?.answered ?? 0} testId="cust-sum-answered" />
      </div>

      {/* 최근 CS 문의 */}
      <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414]" data-testid="simple-customers-inq">
        <div className="p-3 border-b border-black/5 dark:border-white/5 flex items-baseline justify-between">
          <p className="text-sm font-black tracking-widest uppercase flex items-center gap-2">
            <MessageSquare size={14} /> 최근 CS 문의
          </p>
          <button onClick={onOpenDetailedSection} className="text-xs font-bold text-[#FF3B30] hover:underline" data-testid="simple-customers-inq-all">
            전체 문의 보기 →
          </button>
        </div>
        {inquiries.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-8">최근 CS 문의가 없습니다.</p>
        ) : (
          <div className="divide-y divide-black/5 dark:divide-white/5" data-testid="simple-customers-inq-list">
            {inquiries.map((inq) => {
              const buyer = inq.buyer_snapshot?.name || inq.customer_name || '-';
              const st = inq.status || 'new';
              return (
                <button key={inq.inquiry_id} onClick={onOpenDetailedSection}
                  className="w-full text-left px-3 py-3 flex items-center gap-3 hover:bg-black/5 dark:hover:bg-white/5"
                  data-testid={`simple-customers-inq-row-${inq.inquiry_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-bold">{buyer}</span>
                      {inq.order_number && <span className="text-[11px] text-gray-500">· {inq.order_number}</span>}
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${STATUS_CLS[st] || STATUS_CLS.new}`}>{STATUS_LABEL[st] || st}</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10">{CATEGORY_LABEL[inq.category] || inq.category}</span>
                    </div>
                    <p className="text-sm truncate mt-1 font-semibold">{inq.title || '(제목 없음)'}</p>
                  </div>
                  <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="text-center pt-2">
        <button onClick={onOpenDetailedSection} className="text-xs font-bold text-gray-500 hover:text-[#FF3B30] hover:underline" data-testid="simple-customers-detail-bottom">
          고객 목록 · CS 처리이력 · 메모 <b>상세 관리 →</b>
        </button>
      </div>
    </div>
  );
}

const SumBox = ({ label, value, highlight, testId }) => (
  <div className={`rounded-lg border p-3 ${highlight ? 'border-[#FF3B30]/40 bg-[#FF3B30]/5' : 'border-black/5 dark:border-white/10 bg-white dark:bg-[#141414]'}`} data-testid={testId}>
    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</p>
    <p className={`mt-1 text-xl font-black tabular-nums ${highlight ? 'text-[#FF3B30]' : ''}`}>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </p>
  </div>
);
